import React, { useEffect, useState, useRef } from "react";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import * as constants from "../../constants/CONSTANT";

const IncomingCalls = ({
  socket,
  selectedBusinessNumber,
  showCallUI,
  setCallEventReceived,
}) => {
  const [show, setShow] = useState(false);
  const [callData, setCallData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [callStatus, setCallStatus] = useState("ringing");
  const [inCall, setInCall] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  const audioRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const pcRef = useRef(null);
  const timerRef = useRef(null);
  const timeoutRef = useRef(null);

  const ringtoneMp3 = `${constants.FRONTEND_URL}/user_images/ringtone.mp3`;

  useEffect(() => {
    if (!socket) return;

    const handleCallEvent = async (payload) => {
      // console.log("payload", payload);
      const call = payload?.data;
      if (!call || call.business_number !== selectedBusinessNumber) return;

      if (payload?.type === "call" && call.direction === "USER_INITIATED") {
        setCallEventReceived?.(true);
        const isNewCall = !callData || call.call_id !== callData.call_id;

        // INCOMING - USER_INITIATED
        if (
          (call.event === "offer" || call.event === "connect") &&
          call.direction === "USER_INITIATED"
        ) {
          if (isNewCall) {
            setupIncomingCall(call);
          }
        }
        if (call.event === "terminate") {
          endCallCleanup();
        }
      }
    };

    socket.on("whatsapp_call_event", handleCallEvent);
    return () => {
      socket.off("whatsapp_call_event", handleCallEvent);
      clearTimeout(timeoutRef.current);
      clearInterval(timerRef.current);
    };
  }, [socket, selectedBusinessNumber, callData]);

  useEffect(() => {
    // console.log("socket", socket);
    if (!socket) return;

    socket.on("call_accepted_elsewhere", (payload) => {
      // console.log("payload", payload);

      const myDeviceId = localStorage.getItem("deviceId");
      // console.log("payload",payload);
      // console.log("callData",callData)
      // console.log("myDeviceId",myDeviceId)

      if (
        payload.call_id === callData?.call_id &&
        payload.business_number === callData?.business_number &&
        payload.accepted_by !== myDeviceId
      ) {
        endCallCleanup();
      }
    });

    return () => {
      socket.off("call_accepted_elsewhere");
    };
  }, [socket, callData]);

  const setupIncomingCall = (call) => {
    setCallData(call);
    setCallStatus("ringing");
    setShow(true);
    setInCall(false);
    setCallDuration(0);

    const audio = audioRef.current;
    if (audio) {
      audio.src = ringtoneMp3;
      audio.load();
      audio.volume = 1;
      audio.muted = false;

      audio.play().catch((err) => {
        console.warn("Autoplay blocked", err);
        alert("Allow autoplay audio permission in your browser.");
      });
    }

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      handleReject();
    }, 60000);
  };

  const handleAccept = async () => {
    setIsProcessing(true);
    audioRef.current?.pause();
    clearTimeout(timeoutRef.current);

    try {
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });
      pcRef.current = pc;

      pc.ontrack = (event) => {
        remoteAudioRef.current.srcObject = event.streams[0];
        remoteAudioRef.current.muted = false;
        remoteAudioRef.current.play();
      };

      await pc.setRemoteDescription({ type: "offer", sdp: callData.sdp });

      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      localStream
        .getTracks()
        .forEach((track) => pc.addTrack(track, localStream));

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      const modifiedSdp = answer.sdp.replace(
        "a=setup:actpass",
        "a=setup:active"
      );
      const payload = {
        messaging_product: "whatsapp",
        call_id: callData.call_id,
        action: "accept",
        session: {
          sdp_type: "answer",
          sdp: modifiedSdp,
        },
      };
      await WhatsAppAPI.acceptCall(payload, callData.business_number);
      socket.emit("accept_call", {
        call_id: callData.call_id,
        business_number: callData.business_number,
        deviceId: localStorage.getItem("deviceId"),
      });

      setInCall(true);
      setCallStatus("connected");

      timerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Accept error:", error);
      endCallCleanup();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    setIsProcessing(true);
    audioRef.current?.pause();
    clearTimeout(timeoutRef.current);
    clearInterval(timerRef.current);

    try {
      await WhatsAppAPI.rejectCall({
        call_id: callData?.call_id,
        business_number: callData?.business_number,
      });
    } catch (err) {
      console.error("Reject error:", err);
    } finally {
      endCallCleanup();
    }
  };

  const toggleMute = () => {
    const newMute = !isMuted;
    setIsMuted(newMute);
    if (pcRef.current) {
      pcRef.current
        .getSenders()
        .filter((sender) => sender.track?.kind === "audio")
        .forEach((sender) => {
          sender.track.enabled = !newMute;
        });
    }
  };

  const endCallCleanup = () => {
    // console.log("Ending call and cleaning up...");
    audioRef.current?.pause();
    clearTimeout(timeoutRef.current);
    clearInterval(timerRef.current);
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    setCallEventReceived?.(false);
    setShow(false);
    setCallData(null);
    setCallStatus("ended");
    setInCall(false);
    setCallDuration(0);
    setIsProcessing(false);
  };

  const formatDuration = (seconds) => {
    const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${mins}:${secs}`;
  };

  if (!show || !showCallUI) return null;

  const styles = {
    container: {
      position: "fixed",
      bottom: 20,
      right: 20,
      width: 320,
      height: 480,
      background: "#111",
      borderRadius: 16,
      boxShadow: "0 0 20px rgba(0,0,0,0.5)",
      zIndex: 9999,
      padding: 20,
      color: "#fff",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    },
    avatar: {
      width: 80,
      height: 80,
      backgroundColor: "#0f8d7d",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 32,
      fontWeight: "bold",
      margin: "0 auto 20px",
    },
    callerInfo: {
      marginBottom: 20,
    },
    buttons: {
      display: "flex",
      gap: 20,
      justifyContent: "center",
    },
    button: {
      padding: "10px 20px",
      fontSize: 14,
      border: "none",
      borderRadius: "30px",
      minWidth: 90,
      cursor: "pointer",
    },
    accept: { backgroundColor: "green", color: "#fff" },
    decline: { backgroundColor: "red", color: "#fff" },
    mute: { backgroundColor: "gray", color: "#fff" },
    status: {
      fontStyle: "italic",
      color: "#ccc",
      marginTop: 8,
    },
  };

  return (
    <div style={styles.container}>
      <audio ref={audioRef} loop />
      <audio ref={remoteAudioRef} autoPlay playsInline />

      <div style={styles.avatar}>
        <span>{callData?.name?.[0]?.toUpperCase() || "U"}</span>
      </div>

      <div style={styles.callerInfo}>
        <h4>{callData?.name || "Call In Progress"}</h4>
        <p>{callData?.whatsapp_number}</p>
        <p style={styles.status}>
          {inCall ? formatDuration(callDuration) : callStatus}
        </p>
      </div>

      <div style={styles.buttons}>
        {!inCall ? (
          <>
            <button
              style={{ ...styles.button, ...styles.decline }}
              onClick={handleReject}
            >
              Decline
            </button>
            {callData?.direction === "USER_INITIATED" && (
              <button
                style={{ ...styles.button, ...styles.accept }}
                onClick={handleAccept}
              >
                Accept
              </button>
            )}
          </>
        ) : (
          <>
            <button
              style={{ ...styles.button, ...styles.decline }}
              onClick={handleReject}
            >
              End
            </button>
            <button
              style={{ ...styles.button, ...styles.mute }}
              onClick={toggleMute}
            >
              {isMuted ? "Unmute" : "Mute"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default IncomingCalls;
